import { Loader2 } from 'lucide-react';
import { ScriptPanel } from './v2/ScriptPanel';
import { MediaPreview } from './v2/MediaPreview';
import { RightSidebar } from './v2/RightSidebar';
import { SceneFilmstrip } from './v2/SceneFilmstrip';
import { AudioTimeline } from './v2/AudioTimeline';
import type { IScriptPanelProps, IRightSidebarProps } from '@/@types/sceneEditorV2';
import type { IReelScene } from '@/@types/reelScenes';
import type { FrameMode } from '@/@types/sceneEditorV2';
import type { VideoThumbnailSelection } from '@/@types/sceneEditorRedesign';

function SplittingProgress() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center" role="status">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <h3 className="mt-4 text-lg font-medium text-foreground">Splitting Scenes...</h3>
      <p className="mt-2 max-w-sm text-center text-sm text-foreground-muted">
        AI is analyzing your reel script and creating production-ready scenes. This may take 30-60
        seconds.
      </p>
    </div>
  );
}

interface ISceneEditorBodyProps {
  isSplitting: boolean;
  isAutoSplitting: boolean;
  sortedScenes: IReelScene[];
  selectedIndex: number;
  selectedScene: IReelScene | null;
  selectScene: (index: number) => void;
  frameMode: FrameMode;
  firstFrameUrl: string | null;
  lastFrameUrl: string | null;
  videoUrl: string | null;
  avatarImageUrl: string | null;
  selectedThumbnail: VideoThumbnailSelection;
  onSelectThumbnail: (sel: VideoThumbnailSelection) => void;
  isGeneratingSelectedSceneImages: boolean;
  isGeneratingSelectedSceneVideo: boolean;
  isBurningCaptions: boolean;
  isRendering: boolean;
  totalDuration: number;
  timelineCurrentTime: number;
  isTimelinePlaying: boolean;
  onTimelinePlayPause: () => void;
  onTimelineSeek: (time: number) => void;
  sceneThumbnailUrls: Map<string, string>;
  scriptPanelProps: Omit<IScriptPanelProps, 'scenes' | 'selectedSceneIndex' | 'onSelectScene'>;
  sidebarProps: Omit<IRightSidebarProps, 'scene' | 'sceneIndex'>;
}

export function SceneEditorBody({
  isSplitting,
  isAutoSplitting,
  sortedScenes,
  selectedIndex,
  selectedScene,
  selectScene,
  frameMode,
  firstFrameUrl,
  lastFrameUrl,
  videoUrl,
  avatarImageUrl,
  selectedThumbnail,
  onSelectThumbnail,
  isGeneratingSelectedSceneImages,
  isGeneratingSelectedSceneVideo,
  isBurningCaptions,
  isRendering,
  totalDuration,
  timelineCurrentTime,
  isTimelinePlaying,
  onTimelinePlayPause,
  onTimelineSeek,
  sceneThumbnailUrls,
  scriptPanelProps,
  sidebarProps,
}: ISceneEditorBodyProps) {
  return (
    <div className="flex flex-1 overflow-hidden">
      <ScriptPanel
        scenes={sortedScenes}
        selectedSceneIndex={selectedIndex}
        onSelectScene={selectScene}
        {...scriptPanelProps}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {isSplitting || isAutoSplitting ? (
          <SplittingProgress />
        ) : (
          <>
            <MediaPreview
              scene={selectedScene}
              frameMode={frameMode}
              firstFrameUrl={firstFrameUrl}
              lastFrameUrl={lastFrameUrl}
              videoUrl={videoUrl}
              avatarImageUrl={avatarImageUrl}
              selectedThumbnail={selectedThumbnail}
              onSelectThumbnail={onSelectThumbnail}
              isGeneratingImages={isGeneratingSelectedSceneImages}
              isGeneratingVideo={isGeneratingSelectedSceneVideo || isRendering || isBurningCaptions}
            />
            <AudioTimeline
              totalDuration={totalDuration}
              currentTime={timelineCurrentTime}
              isPlaying={isTimelinePlaying}
              onPlayPause={onTimelinePlayPause}
              onSeek={onTimelineSeek}
            />
            <SceneFilmstrip
              scenes={sortedScenes}
              selectedIndex={selectedIndex}
              onSelectScene={selectScene}
              thumbnailUrls={sceneThumbnailUrls}
            />
          </>
        )}
      </div>

      <RightSidebar scene={selectedScene} sceneIndex={selectedIndex} {...sidebarProps} />
    </div>
  );
}
